import AlertBox from '@/components/AlertBox';
import Loader from '@/components/Loader';
import { NotFoundRedirectionWrapper } from '@/components/NotFoundRedirectWrapper';
import ToolBar from '@/components/ToolBar';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/use-toast';
import { displayError } from '@/lib/error';
import { combinedDateTime, displayFullName, formatHeightForInput, parseHeightToDecimalFeet } from '@/lib/utils';
import { useGetAttemptsByWorkOrderId, useUpdateWorkOrderAttemptMutation } from '@/services/attemptService';
import { useGetWorkOrderById } from '@/services/workOrderService';
import { uploadMedia } from '@/services/uploadMedia';
import type {
  AttemptDetails,
  WorkOrderAttemptCreateRequest,
  WorkOrderAttemptUpdateRequest,
} from '@/types/workOrderAttempt.types';
import { zodResolver } from '@hookform/resolvers/zod';
import { WorkOrderAttemptsSchema } from '@pages/workOrderAttempts/attemptsSchema';
import { WorkOrderAttemptsForm } from '@pages/workOrderAttempts/WorkOrderAttemptsForm';
import { canCreatePsAttempt } from '@pages/workOrderAttempts/canCreatePsAttempt';
import {
  resolvePsCaseTypeId,
  shouldUsePsFirstAttemptHiEdit,
  shouldUsePsFirstAttemptHiStepper,
  shouldUsePsFirstAttemptHiView,
} from '@pages/workOrderAttempts/psFirstAttemptHiConfig';
import { PsFirstAttemptHiStepper } from '@pages/workOrderAttempts/PsFirstAttemptHiStepper';
import { PsFirstAttemptHiView } from '@pages/workOrderAttempts/PsFirstAttemptHiView';
import { useUserStore } from '@/stores/user';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';

export const WorkOrderAttempts = () => {
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode');
  const attemptMode = searchParams.get('attemptMode');
  const attemptId = searchParams.get('attemptId');
  const form = useForm({
    resolver: zodResolver(WorkOrderAttemptsSchema),
    mode: 'onBlur',
  });

  const { getValues } = form;
  const updateAttemptMutation = useUpdateWorkOrderAttemptMutation();
  const [isMediaUploading, setIsMediaUploading] = useState(false);
  const { id } = useParams();
  const loggedInUser = useUserStore((state) => state.user);

  const { data, isPending: isAttemptPending, isError } = useGetAttemptsByWorkOrderId(id);
  const { data: workOrderDetail, isPending: isWorkOrderPending, isError: isWorkOrderError } = useGetWorkOrderById(id);
  const [alertBoxOpen, setAlertBoxOpen] = useState(false);
  const navigate = useNavigate();

  const existingAttemptCount = data?.workOrderAttempts?.length ?? 0;
  // API contract uses linkedHIWorkOrderID; Prisma field is linkedHIWorkOrderId (defensive read).
  const linkedHIWorkOrderID =
    workOrderDetail?.linkedHIWorkOrderID ??
    (workOrderDetail as { linkedHIWorkOrderId?: string | null } | undefined)?.linkedHIWorkOrderId;
  const caseTypeId = resolvePsCaseTypeId(workOrderDetail?.service?.case);
  const eligibilityReady = Boolean(data) && !isAttemptPending && !isWorkOrderPending;
  const isCreatingFirstAttempt = mode === 'edit' && (attemptMode === 'create' || existingAttemptCount === 0);

  const useExpandedHiStepper =
    eligibilityReady &&
    isCreatingFirstAttempt &&
    shouldUsePsFirstAttemptHiStepper({
      existingAttemptCount,
      linkedHIWorkOrderID,
      caseTypeId,
    });

  const useHiView =
    eligibilityReady &&
    shouldUsePsFirstAttemptHiView({
      mode,
      attemptId,
      workOrderAttempts: data?.workOrderAttempts,
      homeInspectionAttempts: data?.homeInspectionAttempts,
      linkedHIWorkOrderID,
    });

  const useHiEdit =
    eligibilityReady &&
    shouldUsePsFirstAttemptHiEdit({
      mode,
      attemptMode,
      attemptId,
      workOrderAttempts: data?.workOrderAttempts,
      homeInspectionAttempts: data?.homeInspectionAttempts,
      linkedHIWorkOrderID,
    });

  useEffect(() => {
    form.reset({
      ...data,
      attempts:
        data?.workOrderAttempts.map((attempt) => ({
          ...attempt,
          serverName: attempt?.server ? displayFullName(attempt.server) : attempt?.vendor ? attempt?.vendor.name : '',
          dateOfAttempt: attempt.dateOfAttempt ? new Date(attempt.dateOfAttempt) : undefined,
          timeOfAttempt: attempt.dateOfAttempt ? new Date(attempt.dateOfAttempt) : undefined,
          workOrderMedia: attempt.workOrderAttemptMedia,
          age: String(attempt.age) ?? '',
          height: formatHeightForInput(attempt.height),
          weight: String(attempt.weight) ?? '',
          emailSentDate: attempt.emailSentDate ? new Date(attempt.emailSentDate) : undefined,
          emailAcknowledgementDate: attempt.emailAcknowledgementDate
            ? new Date(attempt.emailAcknowledgementDate)
            : undefined,
          remarks: attempt.remarks,
          additionalInformation: attempt.additionalInformation,
          trackingNumber: attempt.trackingNumber,
          emailAddress: attempt.emailAddress,
          images: attempt.workOrderAttemptMedia?.map((media) => ({
            id: media.mediaId,
            name: media.media?.metadata.fileName ?? 'Image',
            image: null,
            mediaId: media.mediaId,
            mediaData: media.media,
          })),
        })) ?? [],
    });
  }, [data, mode]);

  if (!canCreatePsAttempt(loggedInUser) && mode === 'edit') {
    return <Navigate to="/not-found" />;
  }

  // Only block on initial load — never unmount the stepper on background refetch
  // (that remounts useGetWorkOrderById and loops forever with staleTime: 0).
  // useHiEdit is false until eligibilityReady, so also gate on edit+attemptId for the spinner.
  const mayLoadHiEdit = mode === 'edit' && Boolean(attemptId) && attemptMode !== 'create';
  if ((isCreatingFirstAttempt || mayLoadHiEdit || mode === 'view') && (isAttemptPending || isWorkOrderPending)) {
    return (
      <NotFoundRedirectionWrapper isItemNotFound={isError} itemName="Work Order">
        <Loader isLoading>{null}</Loader>
      </NotFoundRedirectionWrapper>
    );
  }

  if (useHiView && data?.number) {
    if (isWorkOrderError || !workOrderDetail) {
      return (
        <NotFoundRedirectionWrapper isItemNotFound={isError || isWorkOrderError} itemName="Work Order">
          <div className="flex items-center justify-center h-64 text-error text-sm">
            Failed to load work order. Please go back and try again.
          </div>
        </NotFoundRedirectionWrapper>
      );
    }
    return (
      <NotFoundRedirectionWrapper isItemNotFound={isError} itemName="Work Order">
        <PsFirstAttemptHiView
          caseTypeId={caseTypeId}
          workOrderNumber={data.number}
          attemptList={data}
          workOrder={workOrderDetail}
        />
      </NotFoundRedirectionWrapper>
    );
  }

  if (useHiEdit && linkedHIWorkOrderID && data?.number) {
    if (isWorkOrderError || !workOrderDetail) {
      return (
        <NotFoundRedirectionWrapper isItemNotFound={isError || isWorkOrderError} itemName="Work Order">
          <div className="flex items-center justify-center h-64 text-error text-sm">
            Failed to load work order. Please go back and try again.
          </div>
        </NotFoundRedirectionWrapper>
      );
    }
    return (
      <NotFoundRedirectionWrapper isItemNotFound={isError} itemName="Work Order">
        <PsFirstAttemptHiStepper
          mode="edit"
          linkedHIWorkOrderID={linkedHIWorkOrderID}
          caseTypeId={caseTypeId}
          workOrderNumber={data.number}
          attemptList={data}
        />
      </NotFoundRedirectionWrapper>
    );
  }

  if (useExpandedHiStepper && linkedHIWorkOrderID && data?.number) {
    return (
      <NotFoundRedirectionWrapper isItemNotFound={isError} itemName="Work Order">
        <PsFirstAttemptHiStepper
          linkedHIWorkOrderID={linkedHIWorkOrderID}
          caseTypeId={caseTypeId}
          workOrderNumber={data.number}
        />
      </NotFoundRedirectionWrapper>
    );
  }

  const processAttemptImages = async (attempt: AttemptDetails): Promise<AttemptDetails> => {
    if (!attempt.images) return attempt;

    const updatedImages = await Promise.all(
      attempt.images.map(async (img) => {
        if (img.mediaId) {
          return img;
        }

        if (img.image && !img.mediaId && img.image instanceof File) {
          const uploaded = await uploadMedia(img.image, 'WORK_ORDER_ATTEMPT');
          if (uploaded?.mediaId) {
            return { ...img, mediaId: uploaded.mediaId };
          } else {
            throw new Error('Upload failed');
          }
        }

        return img;
      })
    );

    return {
      ...attempt,
      images: updatedImages,
    };
  };

  const buildAttemptPayload = async (attempts: AttemptDetails[], dirtyAttempts: AttemptDetails[]) => {
    const create: WorkOrderAttemptCreateRequest[] = [];
    const update: WorkOrderAttemptUpdateRequest[] = [];

    for (const [index, attempt] of attempts.entries()) {
      const processed = await processAttemptImages(attempt);
      if (!processed.attemptId) {
        create.push({
          ...processed,
          dateOfAttempt: combinedDateTime(processed.dateOfAttempt, processed.timeOfAttempt),
          age: processed.age ? Number(processed.age) : null,
          height: parseHeightToDecimalFeet(processed.height),
          weight: processed.weight ? Number(processed.weight) : null,
          mediaIds: processed.images?.map((img) => img.mediaId!).filter(Boolean) ?? [],
        });
      } else if (dirtyAttempts?.[index]) {
        const dirtyFields = (
          form.formState.dirtyFields.attempts as Partial<Record<keyof AttemptDetails, boolean>>[] | undefined
        )?.[index];
        if (!dirtyFields) continue;
        const dirtyData = { attemptId: processed.attemptId } as WorkOrderAttemptUpdateRequest;

        Object.keys(dirtyFields).forEach((key) => {
          const typedKey = key as keyof AttemptDetails;
          if (dirtyFields[typedKey]) {
            if (typedKey === 'dateOfAttempt' || typedKey === 'timeOfAttempt') {
              if (dirtyFields.dateOfAttempt ?? dirtyFields.timeOfAttempt) {
                dirtyData.dateOfAttempt = combinedDateTime(processed.dateOfAttempt, processed.timeOfAttempt);
              }
            } else if (typedKey === 'workOrderMedia' || typedKey === 'images') {
              dirtyData.mediaIds = {
                create:
                  processed.images
                    ?.filter((img) => img.mediaId && img.image instanceof File)
                    .map((img) => img.mediaId!) ?? [],
                delete:
                  data?.workOrderAttempts
                    ?.find((a) => a.attemptId === processed.attemptId)
                    ?.workOrderAttemptMedia?.filter((m) => !processed.images?.some((img) => img.mediaId === m.mediaId))
                    ?.map((m) => m.attemptMediaId) ?? [],
              };
            } else if (typedKey === 'age' || typedKey === 'weight') {
              (dirtyData as unknown as Record<string, unknown>)[typedKey] = processed[typedKey]
                ? Number(processed[typedKey])
                : null;
            } else if (typedKey === 'height') {
              (dirtyData as unknown as Record<string, unknown>)[typedKey] = processed[typedKey]
                ? parseHeightToDecimalFeet(processed[typedKey])
                : null;
            } else {
              (dirtyData as unknown as Record<string, unknown>)[typedKey] = processed[typedKey] ?? null;
            }
          }
        });
        update.push(dirtyData);
      }
    }

    return { create, update };
  };

  const saveHandler = async () => {
    try {
      setIsMediaUploading(true);

      const formData = getValues();
      const attempts: AttemptDetails[] = (formData.attempts as AttemptDetails[]) ?? [];

      const dirtyAttempts = form.formState.dirtyFields.attempts as unknown as AttemptDetails[];
      const { create, update } = await buildAttemptPayload(attempts, dirtyAttempts);
      if (id) {
        await updateAttemptMutation.mutateAsync(
          {
            workOrderId: id,
            data: {
              create: create,
              update: update,
            },
          },
          {
            onError: (error) => {
              setIsMediaUploading(false);
              displayError(error.response?.data);
            },
            onSuccess: (data) => {
              setIsMediaUploading(false);
              toast({ description: data.message });
            },
          }
        );
        setAlertBoxOpen(false);
        setIsMediaUploading(false);
        navigate(`/process?workOrderId=${id}`);
      }
    } catch {
      // Error handling is done in uploadMedia and mutation's onError
    } finally {
      setIsMediaUploading(false);
      setAlertBoxOpen(false);
    }
  };

  return (
    <NotFoundRedirectionWrapper isItemNotFound={isError} itemName="Work Order">
      <div className="w-full scrollbar-hide flex flex-col">
        <Loader isLoading={isAttemptPending}>
          <div className="w-full flex flex-row justify-between">
            <ToolBar title={`W/O: ${data?.number} - Status Updates`} />
            {mode === 'view' && (
              <Button
                className=" h-[36px] p-3 w-fit"
                onClick={() => {
                  const newSearchParams = new URLSearchParams(searchParams);
                  newSearchParams.set('mode', 'edit');
                  navigate(`?${newSearchParams.toString()}`, { replace: true });
                }}
              >
                Edit Attempt
              </Button>
            )}
          </div>
          <FormProvider {...form}>
            <WorkOrderAttemptsForm
              mode={mode}
              existingAttempts={data?.workOrderAttempts}
              workOrder={{ server: data?.server, vendor: data?.vendor?.vendor }}
            />
          </FormProvider>
          <div className={`flex justify-end mt-4 mb-8 w-full gap-2`}>
            {mode === 'edit' ? (
              <>
                <AlertBox
                  triggerButton={
                    <Button variant="outline" className="w-[125px] h-[48px] ">
                      Cancel
                    </Button>
                  }
                  title="Are you sure you want to cancel?"
                  description="All of your current changes will be lost."
                  warning={true}
                  cancelBtnLabel="No"
                  confirmBtnLabel="Yes"
                  confirmBtnClick={() => {
                    navigate(`/process?workOrderId=${id}`);
                  }}
                />
                <AlertBox
                  open={alertBoxOpen}
                  triggerButton={
                    <Button
                      className=" w-[125px] h-[48px]"
                      onClick={() => {
                        setAlertBoxOpen(true);
                      }}
                      disabled={
                        !form.formState.isValid ||
                        updateAttemptMutation.isPending ||
                        isMediaUploading ||
                        form.formState.isDirty === false
                      }
                      isLoading={isMediaUploading}
                    >
                      Save
                    </Button>
                  }
                  title="Do you want to save the record?"
                  description="This action will create a new attempt in the database."
                  cancelBtnLabel="Cancel"
                  confirmBtnLabel="Save"
                  cancelBtnClick={() => {
                    setAlertBoxOpen(false);
                  }}
                  isLoading={updateAttemptMutation.isPending || isMediaUploading}
                  confirmBtnClick={saveHandler}
                />
              </>
            ) : (
              <Button
                variant="outline"
                className="w-[125px] h-[48px] "
                onClick={() => navigate(`/process?workOrderId=${id}`)}
              >
                Back
              </Button>
            )}
          </div>
        </Loader>
      </div>
    </NotFoundRedirectionWrapper>
  );
};
